import React, { useState, useEffect, useRef } from 'react';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle, Plus, Trash2, User, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const TempsPresence = ({ user, token, API }) => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    leave_type: 'vacances',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const leaveTypes = [
    { value: 'vacances', label: '🏖️ Vacances', color: 'bg-blue-100 text-blue-800' },
    { value: 'maladie', label: '🤒 Maladie', color: 'bg-red-100 text-red-800' },
    { value: 'conge_personnel', label: '👤 Congé Personnel', color: 'bg-purple-100 text-purple-800' },
    { value: 'autre', label: '📋 Autre', color: 'bg-gray-100 text-gray-800' }
  ];

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      
      // Fetch leave requests
      const requestsResponse = await axios.get(`${API}/leave-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setLeaveRequests(requestsResponse.data);

      // Fetch leave balance
      const balanceResponse = await axios.get(`${API}/leave-balance/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setLeaveBalance(balanceResponse.data);

      // Fetch public holidays
      const holidaysResponse = await axios.get(`${API}/public-holidays`);
      if (isMountedRef.current) setHolidays(holidaysResponse.data);

      // Fetch employees if admin/sub_admin
      if (user.role === 'admin' || user.role === 'sub_admin') {
        const usersResponse = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMountedRef.current) setEmployees(usersResponse.data.filter(u => u.role === 'employee'));
      }

      if (isMountedRef.current) setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement des données');
        setLoading(false);
      }
    }
  };

  const handleCreateLeaveRequest = async () => {
    if (!formData.start_date || !formData.end_date || !formData.reason) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      await axios.post(`${API}/leave-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Demande de congé créée avec succès');
      setDialogOpen(false);
      setFormData({
        leave_type: 'vacances',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la demande');
    }
  };

  const handleReviewLeaveRequest = async (requestId, status, rejectionReason = null) => {
    try {
      await axios.put(
        `${API}/leave-requests/${requestId}/review`,
        { status, rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(status === 'approuve' ? 'Demande approuvée' : 'Demande refusée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la révision');
    }
  };

  const handleDeleteLeaveRequest = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/leave-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Demande supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getLeaveTypeInfo = (type) => {
    return leaveTypes.find(t => t.value === type) || leaveTypes[3];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
      approuve: { label: 'Approuvé', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.en_attente;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  // Filter requests based on role and selected employee
  const filteredRequests = selectedEmployee
    ? leaveRequests.filter(r => r.employee_id === selectedEmployee)
    : leaveRequests;

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Congés Restants</p>
                <p className="text-3xl font-bold text-blue-600">
                  {leaveBalance?.remaining_days || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">jours disponibles</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-600">
                <Calendar className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Congés Utilisés</p>
                <p className="text-3xl font-bold text-green-600">
                  {leaveBalance?.used_days || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">jours pris</p>
              </div>
              <div className="p-3 rounded-lg bg-green-600">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En Attente</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {leaveBalance?.pending_days || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">jours demandés</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-600">
                <AlertCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Annuel</p>
                <p className="text-3xl font-bold text-purple-600">
                  {leaveBalance?.total_days || 18}
                </p>
                <p className="text-xs text-gray-500 mt-1">jours alloués</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-600">
                <CalendarDays className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Demandes de Congé</h2>
          {(user.role === 'admin' || user.role === 'sub_admin') && employees.length > 0 && (
            <Select value={selectedEmployee || 'all'} onValueChange={(val) => setSelectedEmployee(val === 'all' ? null : val)}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les employés</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700">
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle Demande
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle Demande de Congé</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type de Congé</Label>
                <Select value={formData.leave_type} onValueChange={(val) => setFormData({...formData, leave_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date de Début</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label>Date de Fin</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div>
                <Label>Raison</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  placeholder="Expliquez brièvement la raison de votre demande..."
                  rows={4}
                />
              </div>

              <Button onClick={handleCreateLeaveRequest} className="w-full bg-gradient-to-r from-red-600 to-blue-600">
                Soumettre la Demande
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Requests Table */}
      <Card className="border-0 shadow-xl">
        <CardContent className="p-6">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg text-gray-500">Aucune demande de congé</p>
              <p className="text-sm text-gray-400 mt-2">Créez votre première demande pour commencer</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => {
                const typeInfo = getLeaveTypeInfo(request.leave_type);
                return (
                  <Card key={request.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={typeInfo.color}>
                              {typeInfo.label}
                            </Badge>
                            {getStatusBadge(request.status)}
                            <span className="text-sm text-gray-500">
                              {request.days_count} jour{request.days_count > 1 ? 's' : ''}
                            </span>
                          </div>

                          {(user.role === 'admin' || user.role === 'sub_admin') && (
                            <div className="flex items-center gap-2 mb-2">
                              <User className="h-4 w-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-700">{request.employee_name}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              <span>{format(parseISO(request.start_date), 'dd/MM/yyyy', { locale: fr })}</span>
                            </div>
                            <span>→</span>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              <span>{format(parseISO(request.end_date), 'dd/MM/yyyy', { locale: fr })}</span>
                            </div>
                          </div>

                          <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            {request.reason}
                          </p>

                          {request.rejection_reason && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                              <p className="text-sm text-red-800">
                                <strong>Raison du refus :</strong> {request.rejection_reason}
                              </p>
                            </div>
                          )}

                          {request.reviewed_by_name && (
                            <p className="text-xs text-gray-500 mt-2">
                              Révisé par {request.reviewed_by_name} le{' '}
                              {format(parseISO(request.reviewed_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          {(user.role === 'admin' || user.role === 'sub_admin') && request.status === 'en_attente' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleReviewLeaveRequest(request.id, 'approuve')}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  const reason = window.prompt('Raison du refus (optionnel):');
                                  if (reason !== null) {
                                    handleReviewLeaveRequest(request.id, 'refuse', reason);
                                  }
                                }}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Refuser
                              </Button>
                            </>
                          )}
                          
                          {user.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteLeaveRequest(request.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Public Holidays */}
      <Card className="border-0 shadow-xl bg-gradient-to-br from-indigo-50 to-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-indigo-600" />
            Jours Fériés 2025 - Maroc ({holidays.length} jours)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {holidays.map((holiday, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                <div className="p-2 bg-indigo-100 rounded">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{holiday.name}</p>
                  <p className="text-xs text-gray-600">
                    {format(parseISO(holiday.date), 'dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TempsPresence;
